import { defineStore } from 'pinia';
import { ref, computed, shallowRef, triggerRef } from 'vue';
import { DateTime } from 'luxon';

import { useDayApi } from '@/rennovation/common/services/api/useDayApi';
import { usePlanningStore } from './usePlanningStore';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';
import i18n from '@/plugins/i18n';

import { PlanningDayStatus } from '@/rennovation/planning-view/enums/planning-day-status';
import type { ApiDay, DateRange, DutyPeriodSchedule } from '../types/api/days-response';

export interface DayUiItem {
	data: Partial<ApiDay>;
	isoDate: string;
	isHidden: boolean;
}

export interface dayStatusPayload {
	status: string;
	id?: string;
}

type DaysMap = Record<string, DayUiItem>;
type DaysRefMap = Record<string, string>;

interface StoreError {
	active: boolean;
	message: string;
}

interface LoadDaysParams {
	from: string;
	to: string;
}

interface BuildDaysParams {
	days: ApiDay[];
	from: DateTime;
	to: DateTime;
	isInit: boolean;
}

interface SetErrorParams {
	error: boolean;
	message: string;
}

// delete InitDaysParams (unused)

export const DAY_STATUS = {
	LOCKED: PlanningDayStatus.LOCKED,
	PAST: PlanningDayStatus.PAST,
	PUBLISHED: PlanningDayStatus.PUBLISHED,
	PLANNING: PlanningDayStatus.PLANNING,
	OPEN: PlanningDayStatus.OPEN,
} as const;

export type DayRangeStatus = (typeof DAY_STATUS)[keyof typeof DAY_STATUS];

type DayStatus = 'past' | 'present' | 'future' | 'published' | 'planning' | string;

const t = (key: string) => i18n.global.t(key);

export const useDayStore = defineStore('days', () => {
	const daysApi = useDayApi();
	const planningStore = usePlanningStore();
	const alertStore = useAlertStore();

	const days = shallowRef<DaysMap>({});
	const daysRef = shallowRef<DaysRefMap>({});
	const daysIdRef = shallowRef<DaysRefMap>({});

	const publishedDayRange = shallowRef<DateRange | null>(null);
	const planningDayRange = shallowRef<DateRange | null>(null);
	const pastDayRange = shallowRef<DateRange | null>(null);
	const lockedDayRange = shallowRef<DateRange | null>(null);
	const openDayRange = shallowRef<DateRange | null>(null);

	const dateRangesByStatusLoaded = ref(false);
	let daysLoadPromise: Promise<void> | null = null;

	const error = ref<StoreError>({
		active: false,
		message: '',
	});

	const getDayStatus = computed(() => {
		return (day: string): DayStatus => {
			return days.value[day]?.data?.status ?? '';
		};
	});

	const isPastOrPublishedDay = computed(() => {
		return (day: string): boolean => {
			if (!day) return false;
			const status = getDayStatus.value(day);
			return [DAY_STATUS.PAST, DAY_STATUS.PUBLISHED].includes(status);
		};
	});

	const updateDayStatus = async ({ status, id }: dayStatusPayload): Promise<void> => {
		try {
			const result = await daysApi.updateDayStatus(id, { status });
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	};

	function getDateRangeByStatusName(statusName: string): DateRange | null {
		switch (statusName) {
			case DAY_STATUS.PAST:
				return pastDayRange.value;
			case DAY_STATUS.PUBLISHED:
				return publishedDayRange.value;
			case DAY_STATUS.PLANNING:
				return planningDayRange.value;
			case DAY_STATUS.LOCKED:
				return lockedDayRange.value;
			case DAY_STATUS.OPEN:
				return openDayRange.value;
			default:
				return null;
		}
	}

	const isDateInRange = (dateString: string, range?: DateRange | null): boolean => {
		if (!dateString || !range?.from || !range?.to) return false;
		const date = DateTime.fromISO(dateString);
		return date >= DateTime.fromISO(range.from) && date <= DateTime.fromISO(range.to);
	};

	function getDateRangeStatus(dateString: string): DayRangeStatus | null {
		if (isDateInRange(dateString, lockedDayRange.value)) return DAY_STATUS.LOCKED;
		if (isDateInRange(dateString, pastDayRange.value)) return DAY_STATUS.PAST;
		if (isDateInRange(dateString, publishedDayRange.value)) return DAY_STATUS.PUBLISHED;
		if (isDateInRange(dateString, planningDayRange.value)) return DAY_STATUS.PLANNING;
		if (isDateInRange(dateString, openDayRange.value)) return DAY_STATUS.OPEN;
		return null;
	}
	const resetDays = (): void => {
		days.value = {};
		daysRef.value = {};
		daysIdRef.value = {};
		triggerRef(days);
		triggerRef(daysRef);
		triggerRef(daysIdRef);
	};

	const setDays = (data: DaysMap): void => {
		const nextDays = { ...days.value };
		const nextDaysRef = { ...daysRef.value };
		const nextDaysIdRef = { ...daysIdRef.value };

		const keys = Object.keys(data);
		for (let i = 0; i < keys.length; i++) {
			const key = keys[i];
			const dayItem = data[key];
			nextDays[dayItem.isoDate] = dayItem;

			const { id, dateString } = dayItem.data;
			if (id && dateString) {
				nextDaysRef[id] = dateString;
				nextDaysIdRef[dateString] = id;
			}
		}

		days.value = nextDays;
		daysRef.value = nextDaysRef;
		daysIdRef.value = nextDaysIdRef;

		triggerRef(days);
		triggerRef(daysRef);
		triggerRef(daysIdRef);
	};

	const setDayStatus = (day: Pick<ApiDay, 'id' | 'status'>): void => {
		if (day.status) {
			const date = Object.keys(days.value).find((date) => days.value[date].data?.id == day.id);
			if (date) {
				days.value[date].data.status = day.status;
			}
			triggerRef(days);
		}
	};

	const setError = ({ error: isActive, message }: SetErrorParams): void => {
		error.value = {
			active: isActive,
			message,
		};
	};

	async function initDays(): Promise<{ from: DateTime; to: DateTime; dayIds: string[] } | undefined> {
		try {
			resetDays();
			let from = DateTime.fromISO(planningStore.filters.fromDate);
			let to = DateTime.fromISO(planningStore.filters.toDate);

			const daysResponse = await loadDays({
				from: from.toISODate() ?? '',
				to: to.toISODate() ?? '',
			});

			if (!daysResponse?.length) {
				setError({
					error: true,
					message: 'No days returned from API.',
				});
				return;
			}

			if (!to.isValid) {
				to = DateTime.fromISO(daysResponse.at(-1)?.date ?? '');
			}

			if (!from.isValid) {
				from = DateTime.fromISO(daysResponse.at(0)?.date ?? '');
			}

			if (!from.isValid || !to.isValid) {
				setError({
					error: true,
					message: 'No valid date range determined.',
				});
				return;
			}

			// Await the asynchronous chunked builder method
			await buildDays({
				days: daysResponse,
				from,
				to,
				isInit: true,
			});

			return {
				from,
				to,
				dayIds: daysResponse.map((d) => d._id),
			};
		} catch (err) {
			const message = extractErrorMessage({
				error: err,
				fallback: 'Could not initialize the day view.',
			});

			setError({
				error: true,
				message,
			});
		}
	}

	async function buildDays({ days: daysData, from, to, isInit }: BuildDaysParams): Promise<void> {
		const formattedDays: DaysMap = {};

		const responseMap = new Map<string, ApiDay>();
		for (let j = 0; j < daysData.length; j++) {
			const dayObj = daysData[j];
			if (dayObj.dateString) {
				responseMap.set(dayObj.dateString, dayObj);
			}
		}

		const diffInDays = Math.floor(to.diff(from, 'days').days ?? 0);

		for (let i = 0; i <= diffInDays; i++) {
			const currentDay = from.plus({ days: i });
			const isoString = currentDay.toISODate();

			if (!isoString) continue;

			const data: Partial<ApiDay> = responseMap.get(isoString) ?? {};

			formattedDays[isoString] = {
				data,
				isoDate: isoString,
				isHidden: !isInit,
			};

			if (i > 0 && i % 15 === 0) {
				await new Promise((resolve) => setTimeout(resolve, 0));
			}
		}
		setDays(formattedDays);
	}

	async function loadDays({ from, to }: LoadDaysParams): Promise<ApiDay[]> {
		try {
			return await daysApi.findDays(from, to);
		} catch (err) {
			const message = extractErrorMessage({
				error: err,
				fallback: 'Could not load days.',
			});

			setError({
				error: true,
				message,
			});

			throw err;
		}
	}

	function extractErrorMessage({ error, fallback }: { error: unknown; fallback: string }): string {
		if (typeof error === 'object' && error !== null && 'response' in error) {
			const err = error as {
				response?: {
					data?: {
						message?: string;
					};
				};
			};
			return err.response?.data?.message ?? fallback;
		}
		return fallback;
	}

	async function loadDateRangeForDayStatuses(): Promise<DutyPeriodSchedule> {
		try {
			const response = await daysApi.getDateRangeForDayStatuses();
			const { open, published, planning, past, locked } = response;
			publishedDayRange.value = published;
			planningDayRange.value = planning;
			pastDayRange.value = past;
			lockedDayRange.value = locked;
			openDayRange.value = open;
			dateRangesByStatusLoaded.value = true;
			return response;
		} catch (err) {
			const message = extractErrorMessage({
				error: err,
				fallback: 'Could not load days.',
			});

			setError({
				error: true,
				message,
			});

			throw err;
		}
	}

	async function ensureDaysLoaded(range?: { from: string; to: string }): Promise<void> {
		if (Object.keys(days.value).length > 0) {
			return;
		}

		if (daysLoadPromise) {
			return daysLoadPromise;
		}

		daysLoadPromise = (async (): Promise<void> => {
			if (range) {
				planningStore.filters.fromDate = range.from;
				planningStore.filters.toDate = range.to;
			}
			await initDays({});
		})();

		try {
			await daysLoadPromise;
		} finally {
			daysLoadPromise = null;
		}
	}

	return {
		days,
		daysRef,
		daysIdRef,
		error,
		getDayStatus,
		isPastOrPublishedDay,
		publishedDayRange,
		planningDayRange,
		pastDayRange,
		lockedDayRange,
		openDayRange,
		dateRangesByStatusLoaded,
		setDayStatus,
		loadDateRangeForDayStatuses,
		getDateRangeStatus,
		getDateRangeByStatusName,
		initDays,
		ensureDaysLoaded,
		updateDayStatus,
		buildDays,
		loadDays,
		setError,
		setDays,
	};
});
